import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Card,
  Stack,
  Button,
  PasswordInput,
  Alert,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconLock,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const validateNewPassword = (value: string): string | null => {
    if (value.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
    if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
    if (!/\d/.test(value)) return 'Password must contain a number';
    if (!/[^A-Za-z0-9]/.test(value)) return 'Password must contain a special character';
    return null;
  };

  const changePasswordMutation = useMutation({
    mutationFn: () => api.post('/auth/change-password', { currentPassword, newPassword }),
    onSuccess: () => {
      notifications.show({ title: 'Success', message: 'Password changed successfully', color: 'green' });
      navigate('/settings');
    },
    onError: () => {
      setError('Current password is incorrect');
    },
  });

  const handleSubmit = () => {
    setError('');
    if (!currentPassword) { setError('Please enter your current password'); return; }
    const validationError = validateNewPassword(newPassword);
    if (validationError) { setError(validationError); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    changePasswordMutation.mutate();
  };

  if (!user) {
    return (
      <Container size="sm" pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Not logged in">
          You must be logged in to change your password.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="sm" pt="md" pb="xl">
      <Button
        variant="subtle"
        size="compact-sm"
        leftSection={<IconArrowLeft size={14} />}
        onClick={() => navigate('/settings')}
        mb="xs"
      >
        Back to Settings
      </Button>

      <Title order={2} mb="lg">
        Change Password
      </Title>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <IconLock size={24} color="var(--mantine-color-blue-6)" />

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}

          <PasswordInput
            label="Current Password"
            placeholder="Enter your current password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
          />

          <PasswordInput
            label="New Password"
            placeholder="Enter a new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
          />

          <PasswordInput
            label="Confirm New Password"
            placeholder="Confirm your new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
          />

          <Button onClick={handleSubmit} loading={changePasswordMutation.isPending} fullWidth>
            Change Password
          </Button>
        </Stack>
      </Card>
    </Container>
  );
}
